/**
 * admin/makua/StaffBooking.js — a reservation taken by staff: on the
 * phone, by email, at the door (V2.1, Page 5). Same engine as the website
 * and sellers, so it can't take a room or a place that isn't free.
 *
 *   1. Retreat and stay   the dated retreat, the party, and the real
 *                         check-in and check-out dates (the stay covers the
 *                         retreat; staff may add up to 30 nights each side)
 *   2. Guests and room    find someone who has booked before, or add them;
 *                         the room type, and the system's room or staff's own
 *   3. Payment            the bill as it will be saved, and how they'll pay
 */
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { reservationsAdminAPI, scheduledAPI, inventoryAPI } from "../../services/api";
import { useLoad, Field, Modal, range, money, fmtDate, asDate } from "./ops";

const blank = () => ({ first_name: "", last_name: "", email: "", phone: "", person_id: null });
const nightsBetween = (a, b) => (a && b ? Math.round((asDate(b) - asDate(a)) / 864e5) : 0);
const METHOD = { bank: "Bank transfer", card: "Card", crypto: "Crypto", cash: "Cash", other: "Other" };

/**
 * presetUnit/presetDate/presetEnd: started from free nights on the Booking
 * Calendar. The dated retreat around those nights is chosen, the stay
 * stretched to cover both, and that room or bed picked.
 */
export default function StaffBooking({ onClose, onCreated, presetPathwayId = null, presetUnit = null, presetDate = null, presetEnd = null }) {
  const { data: dates } = useLoad(() => scheduledAPI.list({ scope: "upcoming" }), []);
  const [f, setF] = useState({
    pathway_id: presetPathwayId || "", guests_count: 1, arrive_date: "", depart_date: "", room_type_id: "",
    payment_method: "bank", payment_plan: "full", notify: true, admin_notes: "",
  });
  const [people, setPeople] = useState([blank()]);
  const [pick, setPick] = useState(null);      // null = the system chooses; else [{room_id, bed_id}]
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const p = (dates || []).find((d) => String(d.id) === String(f.pathway_id));
  const fromCalendar = useRef(!!presetDate);
  // From the calendar: the retreat running on (or next after) the chosen night.
  useEffect(() => {
    if (!presetDate || !dates || f.pathway_id) return;
    const hit = dates.find((d) => d.start_date <= presetDate && presetDate < d.end_date) || dates.find((d) => d.start_date >= presetDate);
    if (hit) setF((x) => ({ ...x, pathway_id: hit.id, room_type_id: presetUnit?.categoryId ? String(presetUnit.categoryId) : "" }));
  }, [dates]); // eslint-disable-line react-hooks/exhaustive-deps
  // The retreat's own dates are the starting point (stretched to the calendar's nights).
  useEffect(() => {
    if (!p) return;
    if (fromCalendar.current && presetDate) {
      setF((x) => ({ ...x, arrive_date: presetDate < p.start_date ? presetDate : p.start_date, depart_date: presetEnd && presetEnd > p.end_date ? presetEnd : p.end_date }));
    } else setF((x) => ({ ...x, arrive_date: p.start_date, depart_date: p.end_date }));
  }, [p?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const datesOk = f.arrive_date && f.depart_date && f.depart_date > f.arrive_date;
  const { data: free } = useLoad(() => (p && datesOk ? inventoryAPI.free(f.arrive_date, f.depart_date) : Promise.resolve(null)), [p?.id, f.arrive_date, f.depart_date]);
  const cats = useMemo(() => free?.categories || [], [free]);

  // The bill, live: the same calculation the reservation will be saved with.
  const seq = useRef(0);
  useEffect(() => {
    if (!p || !datesOk) { setQuote(null); return undefined; }
    const n = ++seq.current;
    const t = setTimeout(() => {
      reservationsAdminAPI.quote({ pathway_id: p.id, guests_count: f.guests_count, room_type_id: f.room_type_id || null, arrive_date: f.arrive_date, depart_date: f.depart_date })
        .then((r) => { if (n === seq.current) { setQuote(r.data); setQuoteError(""); } })
        .catch((err) => { if (n === seq.current) { setQuote(null); setQuoteError(err.message); } });
    }, 250);
    return () => clearTimeout(t);
  }, [p?.id, f.guests_count, f.room_type_id, f.arrive_date, f.depart_date, datesOk]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPick(null); }, [f.room_type_id, f.arrive_date, f.depart_date, f.guests_count]);
  // The calendar's room or bed, once it's known to be free for the whole stay.
  useEffect(() => {
    if (!fromCalendar.current || !presetUnit || !quote?.freeUnits) return;
    const u = quote.freeUnits.find((x) => x.room_id === presetUnit.roomId && (x.bed_id || null) === (presetUnit.bedId || null));
    if (u && String(quote.category?.id) === String(presetUnit.categoryId)) { setPick([{ room_id: u.room_id, bed_id: u.bed_id }]); fromCalendar.current = false; }
  }, [quote]); // eslint-disable-line react-hooks/exhaustive-deps

  const plans = quote?.plans || [];
  useEffect(() => {
    if (plans.length && !plans.some((x) => x.id === f.payment_plan)) setF((x) => ({ ...x, payment_plan: plans[0].id }));
  }, [plans.map((x) => x.id).join()]); // eslint-disable-line react-hooks/exhaustive-deps

  const setGuests = (n) => {
    const count = Math.max(1, Math.min(Number(p?.max_party) || 20, Number(n) || 1));
    setF({ ...f, guests_count: count });
    setPeople((cur) => Array.from({ length: count }, (_, i) => cur[i] || blank()));
  };
  const setPerson = (i, patch) => setPeople(people.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const nights = nightsBetween(f.arrive_date, f.depart_date);
  const before = p && f.arrive_date ? nightsBetween(f.arrive_date, p.start_date) : 0;
  const after = p && f.depart_date ? nightsBetween(p.end_date, f.depart_date) : 0;
  const beyond = p && (before > p.nights_before || after > p.nights_after);
  const need = quote?.category?.unitsNeeded || 0;
  const plan = plans.find((x) => x.id === f.payment_plan);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const res = await reservationsAdminAPI.create({ ...f, guests: people, units: pick && pick.length ? pick : undefined });
      toast.success(res.message || "Reservation created.");
      onCreated?.(res.data.reservation);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const ok = p && datesOk && f.room_type_id && !quoteError && people[0].email.trim()
    && people.every((g) => g.first_name.trim() && g.last_name.trim()) && (!pick || pick.length === need);

  return (
    <Modal title="New reservation" onClose={onClose} wide>
      <section className="ops-step" aria-labelledby="sb-1">
        <h4 id="sb-1" className="ops-step-title">1. Retreat and stay</h4>
        <div className="ops-grid ops-grid--4">
          <Field label="Dated retreat" className="ops-span-3">
            <select value={f.pathway_id} onChange={(e) => setF({ ...f, pathway_id: e.target.value, room_type_id: "" })}>
              <option value="">Choose…</option>
              {(dates || []).map((d) => <option key={d.id} value={d.id}>{d.name} · {range(d.start_date, d.end_date)} · {Math.max(0, d.seat_limit - d.seats_taken)} left</option>)}
            </select>
          </Field>
          <Field label="Guests" hint={p ? `Up to ${p.max_party} in one booking` : undefined}><input type="number" min="1" max={p?.max_party || 20} value={f.guests_count} onChange={(e) => setGuests(e.target.value)} /></Field>
          {p && (
            <>
              <Field label="Check-in" className="ops-span-2"><input type="date" value={f.arrive_date} max={p.start_date} onChange={(e) => setF({ ...f, arrive_date: e.target.value })} /></Field>
              <Field label="Check-out" className="ops-span-2"><input type="date" value={f.depart_date} min={p.end_date} onChange={(e) => setF({ ...f, depart_date: e.target.value })} /></Field>
            </>
          )}
        </div>
        {p && datesOk && (
          <p className={`ops-muted ${beyond ? "ops-warn" : ""}`} style={{ margin: 0 }}>
            {nights} night{nights === 1 ? "" : "s"}, {fmtDate(f.arrive_date)} to {fmtDate(f.depart_date)}: the retreat ({range(p.start_date, p.end_date)})
            {before > 0 ? `, ${before} night${before === 1 ? "" : "s"} before` : ""}{after > 0 ? `, ${after} after` : ""}.
            {" "}{beyond ? `Guests can only add ${p.nights_before} before and ${p.nights_after} after on the website; this is a staff booking beyond that.` : before || after ? "Within the extra nights guests can book." : ""}
          </p>
        )}
        {quoteError && <p className="ops-error">{quoteError}</p>}
      </section>

      <section className="ops-step" aria-labelledby="sb-2">
        <h4 id="sb-2" className="ops-step-title">2. Guests and room</h4>
        {people.map((g, i) => <GuestRow key={i} i={i} g={g} onChange={(patch) => setPerson(i, patch)} />)}
        {p && datesOk && (
          <div className="ops-grid ops-grid--3">
            <Field label="Room type" hint={`Free for the whole stay, ${range(f.arrive_date, f.depart_date)}`} className="ops-span-2">
              <select value={f.room_type_id} onChange={(e) => setF({ ...f, room_type_id: e.target.value })}>
                <option value="">Choose…</option>
                {cats.map((c) => <option key={c.id} value={c.id} disabled={!c.units.length}>{c.name} · {c.units.length} free {c.sellBy === "bed" ? "beds" : "rooms"}</option>)}
              </select>
            </Field>
            {quote?.category && (
              <div className="ops-field"><span>Room or bed</span>
                <div className="ops-seg" role="group" aria-label="Who chooses the room">
                  <button type="button" aria-pressed={!pick} onClick={() => setPick(null)}>System chooses</button>
                  <button type="button" aria-pressed={!!pick} onClick={() => setPick([])}>I'll choose</button>
                </div>
              </div>
            )}
          </div>
        )}
        {quote?.category && !pick && <p className="ops-muted" style={{ margin: 0 }}>The system gives the party {need} {quote.category.sellBy === "bed" ? "bed" : "room"}{need === 1 ? "" : "s"}, kept together for every night. You can move them afterwards.</p>}
        {quote?.category && pick && (
          <fieldset className="ops-pick">
            <legend>Choose {need} {quote.category.sellBy === "bed" ? "bed" : "room"}{need === 1 ? "" : "s"} ({pick.length} chosen)</legend>
            {!quote.freeUnits.length && <p className="ops-error">Nothing in {quote.category.name} is free for the whole stay.</p>}
            <div className="ops-pick-grid">
              {quote.freeUnits.map((u) => {
                const k = `${u.room_id}:${u.bed_id || ""}`;
                const on = pick.some((x) => `${x.room_id}:${x.bed_id || ""}` === k);
                return (
                  <label key={k} className={`ops-pick-unit ${on ? "is-on" : ""}`}>
                    <input type="checkbox" checked={on} disabled={!on && pick.length >= need}
                      onChange={() => setPick(on ? pick.filter((x) => `${x.room_id}:${x.bed_id || ""}` !== k) : [...pick, { room_id: u.room_id, bed_id: u.bed_id }])} />
                    {u.label}
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}
      </section>

      <section className="ops-step" aria-labelledby="sb-3">
        <h4 id="sb-3" className="ops-step-title">3. Payment</h4>
        <div className="ops-grid ops-grid--3">
          <Field label="How they'll pay">
            <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}>
              {Object.entries(METHOD).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Plan" hint={plans.length < 2 && quote ? "Deposits and instalments need more time before arrival" : undefined}>
            <select value={f.payment_plan} onChange={(e) => setF({ ...f, payment_plan: e.target.value })} disabled={!plans.length}>
              {(plans.length ? plans : [{ id: "full", label: "Pay in full" }]).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </Field>
          <Field label="Staff note"><input value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} /></Field>
        </div>
        {quote && f.room_type_id && (
          <table className="ops-ledger" aria-label="The bill">
            <tbody>
              {quote.lines.map((l, i) => <tr key={i}><td>{l.label}</td><td className="num">{money(l.amount)}</td></tr>)}
              <tr className="ops-ledger-total"><td>Total</td><td className="num">{money(quote.total)}</td></tr>
              {plan && plan.id !== "full" && <tr><td>Due now ({plan.label.toLowerCase()})</td><td className="num">{money(plan.dueNow)}</td></tr>}
            </tbody>
          </table>
        )}
        <label className="ops-check"><input type="checkbox" checked={f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} />Email the lead guest their reservation and how to pay</label>
        <p className="ops-note">The room is held like a website booking until payment is recorded; nothing is confirmed before then. Diets and screening are completed from the guest's account or edited here afterwards.</p>
      </section>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={!ok || saving} onClick={save}>{saving ? "Creating…" : quote && f.room_type_id ? `Create reservation · ${money(quote.total)}` : "Create reservation"}</button></div>
    </Modal>
  );
}

/** One guest: type a name or email to find someone who has booked before, or fill it in. */
function GuestRow({ i, g, onChange }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => reservationsAdminAPI.people(q.trim()).then((r) => setHits(r.data.people || [])).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const use = (h) => {
    onChange({ first_name: h.first_name || "", last_name: h.last_name || "", email: h.email || "", phone: h.phone || "", person_id: h.id });
    setQ(""); setHits([]); setOpenList(false);
  };
  const label = i === 0 ? "Lead guest" : `Guest ${i + 1}`;
  return (
    <div className="ops-guest">
      <div className="ops-guest-find">
        <Field label={`${label}: find someone who has booked before`}>
          <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setOpenList(true); }} placeholder="Name, email or phone"
            role="combobox" aria-expanded={openList && hits.length > 0} aria-controls={`guest-hits-${i}`} aria-autocomplete="list" />
        </Field>
        {openList && hits.length > 0 && (
          <ul id={`guest-hits-${i}`} className="ops-hits" role="listbox">
            {hits.map((h) => (
              <li key={h.id} role="option" aria-selected="false">
                <button type="button" onClick={() => use(h)}>
                  <b>{h.first_name} {h.last_name}</b> · {h.email}
                  <span className="ops-sub">{h.reservations ? `${h.reservations} reservation${h.reservations === 1 ? "" : "s"}` : "no reservations yet"}{h.last_stay ? ` · last stay ${fmtDate(h.last_stay)}` : ""}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {g.person_id && <span className="ops-sub">Linked to their existing account. <button type="button" className="ops-btn--link" onClick={() => onChange({ person_id: null })}>Unlink</button></span>}
      </div>
      <div className="ops-grid ops-grid--4">
        <Field label="First name"><input value={g.first_name} onChange={(e) => onChange({ first_name: e.target.value })} /></Field>
        <Field label="Last name"><input value={g.last_name} onChange={(e) => onChange({ last_name: e.target.value })} /></Field>
        <Field label={i === 0 ? "Email (their account)" : "Email (optional)"}><input type="email" value={g.email} onChange={(e) => onChange({ email: e.target.value, person_id: null })} /></Field>
        <Field label="Phone"><input value={g.phone} onChange={(e) => onChange({ phone: e.target.value })} /></Field>
      </div>
    </div>
  );
}
